import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/UI/Button';
import { conversationService } from '../../services/conversationService';
import { inboxService } from '../../services/inboxService';
import { csatService, CsatSummary } from '../../services/csatService';
import { Conversation } from '../../types/conversation';
import { Inbox } from '../../types/inbox';
import {
  CONVERSATION_STATUS_LABELS,
  ConversationPriority,
  ConversationStatus,
} from '../../types/atendimentoEnums';
import { useToast } from '../../components/UI/ToastProvider';
import { useConversationHub } from '../../hooks/useConversationHub';
import { useAuth } from '../../contexts/AuthContext';
import { helpdeskRoutes } from '../../routes/helpdeskRoutes';
import { OperationalIndicatorsSection } from '../../components/Atendimento/OperationalIndicatorsSection';

const OPEN_STATUSES: ConversationStatus[] = [
  ConversationStatus.Open,
  ConversationStatus.Pending,
  ConversationStatus.WaitingForOperator,
  ConversationStatus.InProgress,
  ConversationStatus.Reopened,
];

interface DashboardStats {
  emAndamento: number;
  naFila: number;
  reabertas: number;
}

interface ReasonBadge {
  label: string;
  color: string;
}

const getWaitingReference = (conversation: Conversation): Date => {
  const raw = conversation.waitingSince || conversation.lastActivityAt;
  return new Date(raw);
};

const sortByLongestWaiting = (conversations: Conversation[]): Conversation[] => {
  return [...conversations].sort((a, b) => {
    return getWaitingReference(a).getTime() - getWaitingReference(b).getTime();
  });
};

const isReopened = (conversation: Conversation): boolean => {
  return Boolean(conversation.parentConversationId) || conversation.status === ConversationStatus.Reopened;
};

const needsAttention = (conversation: Conversation): boolean => {
  if (isReopened(conversation))
  {
    return true;
  }

  if (!conversation.firstReplyAt)
  {
    return true;
  }

  if (conversation.status === ConversationStatus.WaitingForOperator)
  {
    return true;
  }

  if (conversation.priority === ConversationPriority.Urgent || conversation.priority === ConversationPriority.High)
  {
    return true;
  }

  return false;
};

const getReasonBadges = (conversation: Conversation): ReasonBadge[] => {
  const badges: ReasonBadge[] = [];

  if (conversation.priority === ConversationPriority.Urgent)
  {
    badges.push({ label: 'Prioridade urgente', color: 'bg-red-100 text-red-800' });
  }
  else if (conversation.priority === ConversationPriority.High)
  {
    badges.push({ label: 'Prioridade alta', color: 'bg-orange-100 text-orange-800' });
  }

  if (isReopened(conversation))
  {
    badges.push({ label: 'Reaberta', color: 'bg-indigo-100 text-indigo-800' });
  }

  if (!conversation.firstReplyAt)
  {
    badges.push({ label: 'Sem 1ª resposta', color: 'bg-yellow-100 text-yellow-800' });
  }

  if (conversation.status === ConversationStatus.WaitingForOperator)
  {
    badges.push({ label: 'Na fila', color: 'bg-purple-100 text-purple-800' });
  }

  return badges;
};

const formatWaitingTime = (conversation: Conversation): string => {
  const waitingMs = Date.now() - getWaitingReference(conversation).getTime();
  const minutes = Math.max(0, Math.floor(waitingMs / 60000));

  if (minutes < 1)
  {
    return 'Agora';
  }

  if (minutes < 60)
  {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours < 24)
  {
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  return `${days}d`;
};

interface StatCardProps {
  label: string;
  value: number;
  hint: string;
  iconBg: string;
  iconColor: string;
  iconPath: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, hint, iconBg, iconColor, iconPath }) => (
  <div className="bg-white rounded-lg shadow p-5">
    <div className="flex items-center">
      <div className={`p-2.5 rounded-lg ${iconBg}`}>
        <svg className={`h-5 w-5 ${iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
        </svg>
      </div>
      <div className="ml-4 min-w-0">
        <p className="text-2xl font-semibold text-gray-900">{value}</p>
        <p className="text-sm text-gray-700">{label}</p>
        <p className="text-xs text-gray-400 mt-0.5 truncate">{hint}</p>
      </div>
    </div>
  </div>
);

export const AtendimentoDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingNext, setLoadingNext] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({
    emAndamento: 0,
    naFila: 0,
    reabertas: 0,
  });
  const [csatSummary, setCsatSummary] = useState<CsatSummary | null>(null);
  const [indicatorsRefreshToken, setIndicatorsRefreshToken] = useState(0);

  const loadData = useCallback(async () => {
    setLoading(true);
    try
    {
      const [inboxesData, conversationsData, csatData] = await Promise.all([
        inboxService.getAll(),
        conversationService.getAll({
          statuses: OPEN_STATUSES,
        }),
        csatService.getSummary().catch(() => null),
      ]);

      const myInboxIds = inboxesData
        .filter(inbox => user?.id && inbox.memberUserIds.includes(user.id))
        .map(inbox => inbox.id);

      const filtered = myInboxIds.length > 0
        ? conversationsData.filter(c => myInboxIds.includes(c.inboxId))
        : conversationsData;

      setInboxes(inboxesData);
      setConversations(filtered);
      setCsatSummary(csatData);

      setStats({
        emAndamento: filtered.length,
        naFila: filtered.filter(c => c.status === ConversationStatus.WaitingForOperator).length,
        reabertas: filtered.filter(c => isReopened(c)).length,
      });
    }
    catch
    {
      addToast('Erro ao carregar dados', 'error');
    }
    finally
    {
      setLoading(false);
    }
  }, [addToast, user?.id]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useConversationHub({
    callbacks: {
      onConversationListChanged: () => {
        void loadData();
        setIndicatorsRefreshToken((token) => token + 1);
      },
      onConversationUpdated: () => {
        void loadData();
        setIndicatorsRefreshToken((token) => token + 1);
      },
      onConversationAssigned: () => {
        void loadData();
        setIndicatorsRefreshToken((token) => token + 1);
      },
    },
  });

  const handleAttendNext = async () => {
    setLoadingNext(true);
    try
    {
      const nextConversations = await conversationService.getAll({
        statuses: OPEN_STATUSES,
      });

      const myInboxIds = inboxes
        .filter(inbox => user?.id && inbox.memberUserIds.includes(user.id))
        .map(inbox => inbox.id);

      const filtered = myInboxIds.length > 0
        ? nextConversations.filter(c => myInboxIds.includes(c.inboxId))
        : nextConversations;

      const next = sortByLongestWaiting(filtered)[0];

      if (next)
      {
        navigate(helpdeskRoutes.conversation(next.id));
      }
      else
      {
        addToast('Não há conversas pendentes na fila.', 'success');
      }
    }
    catch
    {
      addToast('Erro ao buscar próxima conversa', 'error');
    }
    finally
    {
      setLoadingNext(false);
    }
  };

  const attentionConversations = sortByLongestWaiting(conversations.filter(needsAttention)).slice(0, 5);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard de Atendimento</h1>
        <p className="text-gray-600 text-sm mt-1">
          Visão geral da fila e atalho para o próximo atendimento
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              label="Em andamento"
              value={stats.emAndamento}
              hint="Todas as conversas abertas"
              iconBg="bg-blue-100"
              iconColor="text-blue-600"
              iconPath="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
            <StatCard
              label="Na fila"
              value={stats.naFila}
              hint="Aguardando operador"
              iconBg="bg-purple-100"
              iconColor="text-purple-600"
              iconPath="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
            <StatCard
              label="Reabertas"
              value={stats.reabertas}
              hint="Cliente retomou o atendimento"
              iconBg="bg-orange-100"
              iconColor="text-orange-600"
              iconPath="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </div>

          {csatSummary && (
            <div className="bg-white rounded-lg shadow p-5">
              <h2 className="text-base font-semibold text-gray-900">CSAT</h2>
              <p className="text-sm text-gray-500 mt-0.5 mb-4">
                Pesquisas de satisfação ofertadas, abertas pelo link e respondidas
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Ofertadas</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">{csatSummary.offeredCount}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Abertas</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">{csatSummary.openedCount ?? 0}</p>
                  {typeof csatSummary.openRate === 'number' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Taxa {(csatSummary.openRate * 100).toFixed(0)}%
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Respondidas</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">{csatSummary.completedCount}</p>
                  {typeof csatSummary.responseRate === 'number' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Taxa {(csatSummary.responseRate * 100).toFixed(0)}%
                    </p>
                  )}
                  {typeof csatSummary.completionRateAmongOpened === 'number' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Entre abertas {(csatSummary.completionRateAmongOpened * 100).toFixed(0)}%
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Média</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">
                    {typeof csatSummary.averageRating === 'number'
                      ? csatSummary.averageRating.toFixed(1)
                      : '—'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <OperationalIndicatorsSection refreshToken={indicatorsRefreshToken} />

          <div className="bg-white rounded-lg shadow p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Próximo atendimento</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Abre a conversa com maior tempo de espera na sua fila.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <Link to={helpdeskRoutes.workspace}>
                <Button variant="secondary" size="md">
                  Abrir console
                </Button>
              </Link>
              <Button
                variant="primary"
                size="md"
                onClick={handleAttendNext}
                disabled={loadingNext || stats.emAndamento === 0}
              >
                {loadingNext ? 'Buscando...' : 'Atender próxima'}
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Conversas que precisam de atenção</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Ordenadas pelo maior tempo de espera
                </p>
              </div>
              <Link to={helpdeskRoutes.workspace} className="text-sm text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">
                Ver no console
              </Link>
            </div>

            {attentionConversations.length === 0 ? (
              <div className="p-10 text-center">
                <p className="text-gray-500">Nenhuma conversa pendente nas suas caixas.</p>
                <p className="text-gray-400 text-sm mt-1">Você está em dia com o atendimento.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {attentionConversations.map(conv => {
                  const badges = getReasonBadges(conv);

                  return (
                    <Link
                      key={conv.id}
                      to={helpdeskRoutes.conversation(conv.id)}
                      className="flex items-center justify-between gap-4 px-6 py-3 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-xs font-medium text-gray-400">#{conv.displayId}</span>
                          <span className="font-medium text-gray-900 truncate">
                            {conv.contactName}
                          </span>
                          {badges.map(badge => (
                            <span
                              key={badge.label}
                              className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${badge.color}`}
                            >
                              {badge.label}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{CONVERSATION_STATUS_LABELS[conv.status]}</span>
                          <span>{conv.inboxName}</span>
                          <span>Esperando há {formatWaitingTime(conv)}</span>
                        </div>
                      </div>
                      <svg className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
